import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button
} from '@mui/material';
import {
  LiveTv,
  Close,
  Fullscreen,
  FullscreenExit,
  Radio,
  Splitscreen,
  ChatBubbleOutline,
  VideocamOff,
  Forum
} from '@mui/icons-material';

const LiveStageSection = ({
  liveProgram,
  activeLayer,
  bgLayer1,
  bgLayer2,
  isVideoOpen,
  setIsVideoOpen,
  isTheatreMode,
  setIsTheatreMode
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileViewMode, setMobileViewMode] = useState('split'); // 'split' | 'chat' | 'video'
  const [isLiveDetected, setIsLiveDetected] = useState(false);
  const [twitchPlayerReady, setTwitchPlayerReady] = useState(false);
  const twitchPlayerRef = useRef(null);

  const isLiveDetectedRef = useRef(isLiveDetected);
  isLiveDetectedRef.current = isLiveDetected;

  const isVideoOpenRef = useRef(isVideoOpen);
  isVideoOpenRef.current = isVideoOpen;

  const consecutiveOfflineRef = useRef(0);

  const isDjBgActive = liveProgram?.type === 'program' && liveProgram?.data?.dj?.chat_bg_active;

  // Determine Twitch parent domain
  const hostname = window.location.hostname;
  const twitchParent = hostname === 'localhost' || hostname === '127.0.0.1' ? 'localhost' : 'nostalbeat.com.br';

  // Load Twitch Interactive SDK & Multi-Layer Live Detection
  useEffect(() => {
    const scriptId = 'twitch-embed-sdk';
    let script = document.getElementById(scriptId);

    const initPlayer = () => {
      if (!window.Twitch || !window.Twitch.Player) return;

      const container = document.getElementById('twitch-player-container');
      if (!container || twitchPlayerRef.current) return;

      try {
        const player = new window.Twitch.Player('twitch-player-container', {
          channel: 'Nostalbeat',
          width: '100%',
          height: '100%',
          parent: [twitchParent, 'www.nostalbeat.com.br', 'nostalbeat.com.br'],
          autoplay: true,
          muted: false,
          controls: true
        });

        twitchPlayerRef.current = player;

        player.addEventListener(window.Twitch.Player.READY, () => {
          setTwitchPlayerReady(true);
        });

        // 1. Event listener for stream going ONLINE
        player.addEventListener(window.Twitch.Player.ONLINE, () => {
          console.log('🔴 Twitch stream detected ONLINE via SDK event!');
          consecutiveOfflineRef.current = 0;
          setIsLiveDetected(true);
          setIsVideoOpen(true);
          try {
            player.play();
          } catch (e) {}
        });

        // 2. Event listener for PLAY / PLAYING (video receiving chunks)
        player.addEventListener(window.Twitch.Player.PLAY, () => {
          console.log('🔴 Twitch stream PLAY event detected');
          consecutiveOfflineRef.current = 0;
          setIsLiveDetected(true);
          setIsVideoOpen(true);
        });

        player.addEventListener(window.Twitch.Player.PLAYING, () => {
          console.log('🔴 Twitch stream PLAYING event detected');
          consecutiveOfflineRef.current = 0;
          setIsLiveDetected(true);
          setIsVideoOpen(true);
        });

        // 3. Event listener for stream going OFFLINE
        // Note: We do NOT immediately close isVideoOpen on raw Twitch OFFLINE,
        // because Twitch fires this during connection/buffering. We let debounced polling handle it.
        player.addEventListener(window.Twitch.Player.OFFLINE, () => {
          console.log('⚪ Twitch stream event OFFLINE (debounced, preventing premature close)');
        });
      } catch (err) {
        console.error('Error initializing Twitch Player SDK:', err);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://player.twitch.tv/js/embed/v1.js';
      script.async = true;
      script.onload = initPlayer;
      document.body.appendChild(script);
    } else {
      initPlayer();
    }

    return () => {
      if (twitchPlayerRef.current) {
        try {
          twitchPlayerRef.current = null;
        } catch (e) {}
      }
    };
  }, [twitchParent, setIsVideoOpen]);

  // Active Polling & Keyword Detection ("Ao vivo" / "AOVIVO" / Twitch Uptime API)
  useEffect(() => {
    let isMounted = true;

    const checkLiveStatus = async () => {
      // 1. Check Twitch API via DecAPI (Checks Twitch server directly)
      try {
        const response = await fetch(`https://decapi.me/twitch/uptime/nostalbeat?_=${Date.now()}`);
        if (response.ok) {
          const text = await response.text();
          const isOffline = text.toLowerCase().includes('offline');

          if (!isOffline && text.trim().length > 0) {
            // Stream is ONLINE! (returns e.g. "5 minutes, 12 seconds")
            consecutiveOfflineRef.current = 0;
            if (isMounted) {
              if (!isLiveDetectedRef.current) {
                console.log('🔴 Live confirmed via Twitch API:', text);
                setIsLiveDetected(true);
                setIsVideoOpen(true);
              }
              if (twitchPlayerRef.current) {
                try {
                  twitchPlayerRef.current.play();
                } catch (e) {}
              }
            }
            return;
          } else {
            // Offline response
            consecutiveOfflineRef.current += 1;
            // Only consider offline after at least 6 consecutive checks (30 seconds) of persistent offline!
            if (consecutiveOfflineRef.current >= 6 && isLiveDetectedRef.current) {
              console.log('⚪ Stream persistently offline after 30s of checks');
              if (isMounted) {
                setIsLiveDetected(false);
                setIsVideoOpen(false);
              }
            }
          }
        }
      } catch (e) {
        // Network or fetch error - do not treat as offline
      }

      // 2. Check Twitch Player internal playback stats
      if (twitchPlayerRef.current) {
        try {
          const player = twitchPlayerRef.current;
          const stats = player.getPlaybackStats ? player.getPlaybackStats() : null;
          if (stats && (stats.fps > 0 || stats.bufferSize > 0)) {
            consecutiveOfflineRef.current = 0;
            if (isMounted && !isLiveDetectedRef.current) {
              setIsLiveDetected(true);
              setIsVideoOpen(true);
            }
            return;
          }
        } catch (e) {}
      }

    };

    // Run check immediately on mount
    checkLiveStatus();

    // Poll every 5 seconds for rapid detection when OBS starts
    const interval = setInterval(checkLiveStatus, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [liveProgram, setIsVideoOpen]);

  // When isVideoOpen is manually toggled, try to play
  useEffect(() => {
    if (isVideoOpen && twitchPlayerRef.current) {
      try {
        twitchPlayerRef.current.play();
      } catch (e) {}
    }
  }, [isVideoOpen]);

  // Reset mobile view mode to 'split' whenever video opens
  useEffect(() => {
    if (isVideoOpen) {
      setMobileViewMode('split');
    }
  }, [isVideoOpen]);

  const handleToggleTheatre = () => {
    setIsTheatreMode((prev) => !prev);
  };

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
    if (isTheatreMode) {
      setIsTheatreMode(false);
    }
    if (twitchPlayerRef.current) {
      try {
        twitchPlayerRef.current.pause();
      } catch (e) {}
    }
  };

  return (
    <Paper
      id="live-stage-section"
      elevation={8}
      sx={{
        borderRadius: 3,
        background: { xs: 'rgba(12, 12, 12, 0.94)', md: 'rgba(12, 12, 12, 0.85)' },
        backdropFilter: { xs: 'none', md: 'blur(16px)' },
        border: isLiveDetected || isVideoOpen
          ? '2px solid rgba(255, 107, 53, 0.6)'
          : '2px solid rgba(255, 107, 53, 0.25)',
        boxShadow: isLiveDetected || isVideoOpen
          ? '0 0 35px rgba(255, 107, 53, 0.25), 0 12px 40px rgba(0, 0, 0, 0.8)'
          : '0 8px 32px rgba(0, 0, 0, 0.6)',
        height: {
          xs: 'auto',
          lg: 'clamp(380px, calc(100vh - 210px), 760px)',
        },
        minHeight: {
          xs: 'auto',
          lg: '380px',
        },
        maxHeight: {
          xs: 'none',
          lg: '760px',
        },
        '@media (min-width: 1200px) and (max-height: 680px)': {
          height: 'calc(100vh - 190px)',
          minHeight: '360px',
          maxHeight: '460px',
        },
        '@media (min-width: 1200px) and (min-height: 681px) and (max-height: 780px)': {
          height: 'calc(100vh - 210px)',
          minHeight: '430px',
          maxHeight: '540px',
        },
        '@media (min-width: 1200px) and (min-height: 781px) and (max-height: 900px)': {
          height: 'calc(100vh - 230px)',
          minHeight: '520px',
          maxHeight: '640px',
        },
        '@media (min-width: 1200px) and (min-height: 901px)': {
          height: 'calc(75vh + 30px)',
          minHeight: '620px',
          maxHeight: '780px',
        },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'width, transform',
      }}
    >
      {/* Top Stage Header / Control Bar */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 0.8, sm: 1.2 },
          '@media (max-height: 720px)': {
            py: 0.7,
          },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          borderBottom: '1px solid rgba(255, 107, 53, 0.15)',
          background: 'linear-gradient(90deg, rgba(255, 107, 53, 0.08) 0%, rgba(0, 212, 255, 0.05) 100%)',
          zIndex: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isLiveDetected ? (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#FF1744',
                    animation: 'pulseLive 1.5s infinite',
                    '@keyframes pulseLive': {
                      '0%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0.8)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(255, 23, 68, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0)' },
                    },
                  }}
                />
              }
              label="🔴 AO VIVO NO AR"
              size="small"
              sx={{
                bgcolor: 'rgba(255, 23, 68, 0.2)',
                border: '1px solid #FF1744',
                color: '#FFF',
                fontWeight: 900,
                fontSize: '0.75rem',
                letterSpacing: '1px',
                fontFamily: 'Orbitron, sans-serif',
              }}
            />
          ) : (
            <Chip
              icon={<Radio sx={{ fontSize: '1rem !important', color: '#00D4FF' }} />}
              label="NOSTALBEAT ON-AIR"
              size="small"
              sx={{
                bgcolor: 'rgba(0, 212, 255, 0.12)',
                border: '1px solid rgba(0, 212, 255, 0.4)',
                color: '#00D4FF',
                fontWeight: 800,
                fontSize: '0.75rem',
                letterSpacing: '0.5px',
                fontFamily: 'Orbitron, sans-serif',
              }}
            />
          )}

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              display: { xs: 'none', md: 'block' },
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            {isVideoOpen ? 'Transmissão em Vídeo & Bate-Papo' : 'Interaja no Bate-Papo da Rádio'}
          </Typography>
        </Box>

        {/* Responsive View Mode Selector for Mobile / Tablet (< lg) */}
        {isVideoOpen && !isDesktop && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 2,
              p: 0.3,
              gap: 0.5,
            }}
          >
            <Chip
              icon={<Splitscreen sx={{ fontSize: '0.9rem !important' }} />}
              label="Dividido"
              size="small"
              clickable
              onClick={() => setMobileViewMode('split')}
              sx={{
                height: 26,
                fontSize: '0.72rem',
                fontWeight: mobileViewMode === 'split' ? 700 : 500,
                bgcolor: mobileViewMode === 'split' ? 'rgba(255, 107, 53, 0.35)' : 'transparent',
                color: mobileViewMode === 'split' ? '#FF6B35' : 'rgba(255, 255, 255, 0.7)',
                border: mobileViewMode === 'split' ? '1px solid rgba(255, 107, 53, 0.6)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.25)' },
              }}
            />
            <Chip
              icon={<ChatBubbleOutline sx={{ fontSize: '0.9rem !important' }} />}
              label="Chat"
              size="small"
              clickable
              onClick={() => setMobileViewMode('chat')}
              sx={{
                height: 26,
                fontSize: '0.72rem',
                fontWeight: mobileViewMode === 'chat' ? 700 : 500,
                bgcolor: mobileViewMode === 'chat' ? 'rgba(0, 212, 255, 0.3)' : 'transparent',
                color: mobileViewMode === 'chat' ? '#00D4FF' : 'rgba(255, 255, 255, 0.7)',
                border: mobileViewMode === 'chat' ? '1px solid rgba(0, 212, 255, 0.6)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.2)' },
              }}
            />
            <Chip
              icon={<LiveTv sx={{ fontSize: '0.9rem !important' }} />}
              label="Vídeo"
              size="small"
              clickable
              onClick={() => setMobileViewMode('video')}
              sx={{
                height: 26,
                fontSize: '0.72rem',
                fontWeight: mobileViewMode === 'video' ? 700 : 500,
                bgcolor: mobileViewMode === 'video' ? 'rgba(255, 107, 53, 0.35)' : 'transparent',
                color: mobileViewMode === 'video' ? '#FF6B35' : 'rgba(255, 255, 255, 0.7)',
                border: mobileViewMode === 'video' ? '1px solid rgba(255, 107, 53, 0.6)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.25)' },
              }}
            />
          </Box>
        )}

        {/* Stage Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isVideoOpen && (
            <Tooltip title="Abrir Transmissão de Vídeo">
              <IconButton
                size="small"
                onClick={() => setIsVideoOpen(true)}
                sx={{
                  color: '#FF6B35',
                  border: '1px solid rgba(255, 107, 53, 0.4)',
                  bgcolor: 'rgba(255, 107, 53, 0.1)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  fontFamily: 'Orbitron, sans-serif',
                  '&:hover': {
                    bgcolor: 'rgba(255, 107, 53, 0.25)',
                    boxShadow: '0 0 15px rgba(255, 107, 53, 0.4)',
                  },
                }}
              >
                <LiveTv sx={{ fontSize: '1.1rem', mr: 0.8 }} />
                Vídeo
              </IconButton>
            </Tooltip>
          )}

          {isVideoOpen && (
            <>
              {/* Theatre Mode Toggle (Desktop only) */}
              <Tooltip title={isTheatreMode ? 'Restaurar Tamanho' : 'Modo Cinema (Tela Larga)'}>
                <IconButton
                  size="small"
                  onClick={handleToggleTheatre}
                  sx={{
                    color: '#00D4FF',
                    display: { xs: 'none', lg: 'inline-flex' },
                    '&:hover': { color: '#33DDFF', bgcolor: 'rgba(0, 212, 255, 0.15)' },
                  }}
                >
                  {isTheatreMode ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
              </Tooltip>

              {/* Close Video Button */}
              <Tooltip title="Fechar Vídeo e Expandir Chat">
                <IconButton
                  size="small"
                  onClick={handleCloseVideo}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { color: '#FF1744', bgcolor: 'rgba(255, 23, 68, 0.15)' },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Main Stage Body: Split 40/60 on Desktop when Video is Open */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* VIDEO PANEL */}
        <Box
          sx={{
            // On Desktop: 40% width when open, 0% when closed, with ultra smooth spring transition
            width: {
              xs: '100%',
              lg: isVideoOpen ? '40%' : '0%',
            },
            height: {
              xs: !isVideoOpen
                ? '0px'
                : mobileViewMode === 'chat'
                  ? '0px'
                  : mobileViewMode === 'video'
                    ? '400px'
                    : '240px',
              sm: !isVideoOpen
                ? '0px'
                : mobileViewMode === 'chat'
                  ? '0px'
                  : mobileViewMode === 'video'
                    ? '480px'
                    : '300px',
              md: !isVideoOpen
                ? '0px'
                : mobileViewMode === 'chat'
                  ? '0px'
                  : mobileViewMode === 'video'
                    ? '520px'
                    : '340px',
              lg: '100%',
            },
            minHeight: 0,
            opacity: isVideoOpen && (mobileViewMode !== 'chat' || isDesktop) ? 1 : 0,
            transform: isVideoOpen ? 'scale(1)' : 'scale(0.96)',
            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            overflow: 'hidden',
            display: !isVideoOpen ? 'none' : 'flex',
            flexDirection: 'column',
            bgcolor: '#000',
            borderRight: {
              xs: 'none',
              lg: isVideoOpen ? '2px solid rgba(255, 107, 53, 0.3)' : 'none',
            },
            borderBottom: {
              xs: isVideoOpen && mobileViewMode === 'split' ? '2px solid rgba(255, 107, 53, 0.3)' : 'none',
              lg: 'none',
            },
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Custom NostalBeat Cyberpunk Offline Stage (Shown when Twitch stream is OFFLINE) */}
          {!isLiveDetected && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: '240px', md: '300px', lg: 0 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2.5, md: 4 },
                textAlign: 'center',
                background: 'radial-gradient(ellipse at center, rgba(255, 107, 53, 0.12) 0%, rgba(10, 10, 14, 0.98) 80%)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Perspective grid floor lines */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '120px',
                  backgroundImage:
                    'linear-gradient(to right, rgba(0, 212, 255, 0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 107, 53, 0.08) 1px, transparent 1px)',
                  backgroundSize: '32px 32px',
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  pointerEvents: 'none',
                }}
              />

              {/* Glowing Icon Booth */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 56, md: 72 },
                  height: { xs: 56, md: 72 },
                  borderRadius: '50%',
                  bgcolor: 'rgba(255, 107, 53, 0.12)',
                  border: '2px solid rgba(255, 107, 53, 0.4)',
                  boxShadow: '0 0 25px rgba(255, 107, 53, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <VideocamOff sx={{ fontSize: { xs: '1.7rem', md: '2.2rem' }, color: '#FF6B35' }} />
              </Box>

              {/* Status Chip */}
              <Chip
                icon={<Radio sx={{ fontSize: '0.85rem !important', color: '#00D4FF !important' }} />}
                label="ESTÚDIO OFF-AIR • O SOM NÃO PARA"
                size="small"
                sx={{
                  bgcolor: 'rgba(0, 212, 255, 0.12)',
                  border: '1px solid rgba(0, 212, 255, 0.4)',
                  color: '#00D4FF',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  fontFamily: 'Orbitron, sans-serif',
                  letterSpacing: '0.8px',
                  mb: 1.5,
                }}
              />

              {/* Title & Description */}
              <Typography
                variant="h6"
                sx={{
                  color: '#FFF',
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', md: '1.25rem' },
                  fontFamily: 'Orbitron, sans-serif',
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Câmera ao Vivo Desconectada
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  maxWidth: '380px',
                  lineHeight: 1.5,
                  fontSize: { xs: '0.78rem', md: '0.85rem' },
                  mb: 2,
                }}
              >
                O DJ ainda não iniciou a transmissão em vídeo, mas a programação da rádio e o bate-papo continuam a todo vapor!
              </Typography>

              {/* Equalizer Wave in Offline Card */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 20, gap: '4px', mb: 2.5 }}>
                {[0.6, 1.2, 0.8, 1.5, 0.7, 1.3, 0.5, 1.0].map((duration, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 3,
                      bgcolor: i % 2 === 0 ? '#FF6B35' : '#00D4FF',
                      borderRadius: 1,
                      animation: `pulseSound ${duration}s ease-in-out infinite alternate`,
                      '@keyframes pulseSound': {
                        '0%': { height: '25%', opacity: 0.4 },
                        '100%': { height: '100%', opacity: 0.9 },
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Direct Action Button */}
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (!isDesktop) {
                    setMobileViewMode('chat');
                  } else {
                    setIsVideoOpen(false);
                  }
                }}
                startIcon={<Forum />}
                sx={{
                  bgcolor: '#FF6B35',
                  color: '#FFF',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderRadius: '20px',
                  px: 2.2,
                  py: 0.6,
                  boxShadow: '0 0 16px rgba(255, 107, 53, 0.5)',
                  '&:hover': {
                    bgcolor: '#FF8C5A',
                    boxShadow: '0 0 24px rgba(255, 107, 53, 0.7)',
                  },
                }}
              >
                Focar no Bate-Papo
              </Button>
            </Box>
          )}

          {/* Twitch Player Container (Shown when isLiveDetected is TRUE) */}
          <Box
            id="twitch-player-container"
            sx={{
              width: '100%',
              height: '100%',
              flex: 1,
              display: isLiveDetected ? 'block' : 'none',
              '& iframe': {
                width: '100%',
                height: '100%',
                border: 'none',
              },
            }}
          />

          {/* Fallback Iframe if SDK is loading and isLiveDetected is TRUE */}
          {!twitchPlayerReady && isVideoOpen && isLiveDetected && (
            <iframe
              src={`https://player.twitch.tv/?channel=Nostalbeat&parent=${twitchParent}&parent=www.nostalbeat.com.br&parent=nostalbeat.com.br`}
              height="100%"
              width="100%"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                border: 'none',
                width: '100%',
                height: '100%',
              }}
              title="DJ Live Camera Stream"
            />
          )}

          {/* Quick Floating Chat Pill when in Video-Only mode on Mobile */}
          {isVideoOpen && !isDesktop && mobileViewMode === 'video' && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                right: 12,
                zIndex: 10,
              }}
            >
              <Chip
                icon={<ChatBubbleOutline sx={{ fontSize: '1rem !important', color: '#FFF !important' }} />}
                label="Abrir Chat"
                size="small"
                clickable
                onClick={() => setMobileViewMode('split')}
                sx={{
                  bgcolor: 'rgba(255, 107, 53, 0.95)',
                  backdropFilter: { xs: 'none', md: 'blur(8px)' },
                  color: '#FFF',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.6)',
                  '&:hover': { bgcolor: '#FF6B35' },
                }}
              />
            </Box>
          )}
        </Box>

        {/* CHAT PANEL */}
        <Box
          sx={{
            // On Desktop: 60% width when video is open, 100% width when video is closed
            width: {
              xs: '100%',
              lg: isVideoOpen ? '60%' : '100%',
            },
            height: {
              xs: !isVideoOpen
                ? 'clamp(380px, 60vh, 560px)'
                : mobileViewMode === 'video'
                  ? '0px'
                  : mobileViewMode === 'chat'
                    ? 'clamp(420px, 70vh, 640px)'
                    : 'clamp(340px, 50vh, 480px)',
              sm: !isVideoOpen
                ? 'clamp(400px, 65vh, 600px)'
                : mobileViewMode === 'video'
                  ? '0px'
                  : mobileViewMode === 'chat'
                    ? 'clamp(440px, 70vh, 660px)'
                    : 'clamp(360px, 50vh, 500px)',
              md: !isVideoOpen
                ? 'clamp(420px, 65vh, 640px)'
                : mobileViewMode === 'video'
                  ? '0px'
                  : mobileViewMode === 'chat'
                    ? 'clamp(460px, 70vh, 680px)'
                    : 'clamp(380px, 50vh, 520px)',
              lg: '100%',
            },
            minHeight: {
              xs: !isVideoOpen ? '360px' : mobileViewMode === 'video' ? '0px' : '320px',
              sm: !isVideoOpen ? '380px' : mobileViewMode === 'video' ? '0px' : '340px',
              md: !isVideoOpen ? '400px' : mobileViewMode === 'video' ? '0px' : '360px',
              lg: 0,
            },
            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            display: isVideoOpen && !isDesktop && mobileViewMode === 'video' ? 'none' : 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Audio Playing Indicator Banner when Mobile is in Chat-Only Mode */}
          {isVideoOpen && !isDesktop && mobileViewMode === 'chat' && (
            <Box
              sx={{
                px: 2,
                py: 0.8,
                bgcolor: 'rgba(255, 107, 53, 0.12)',
                borderBottom: '1px solid rgba(255, 107, 53, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#FF1744',
                    animation: 'pulseLive 1.5s infinite',
                    '@keyframes pulseLive': {
                      '0%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0.8)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(255, 23, 68, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0)' },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: '#FFF', fontWeight: 600, fontSize: '0.75rem' }}>
                  Live com áudio ativo em segundo plano
                </Typography>
              </Box>
              <Chip
                label="Ver Vídeo"
                size="small"
                clickable
                onClick={() => setMobileViewMode('split')}
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(255, 107, 53, 0.3)',
                  color: '#FF6B35',
                  '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.5)' },
                }}
              />
            </Box>
          )}

          {/* Background Layer 1 (DJ Dynamic Background) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: bgLayer1,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: activeLayer === 1 ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: 0,
            }}
          />

          {/* Background Layer 2 (DJ Dynamic Background) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: bgLayer2,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: activeLayer === 2 ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: 0,
            }}
          />

          {/* Minnit Chat Iframe */}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              height: '100%',
              minHeight: 0,
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <iframe
              title="NostalBeat Chat"
              src={`https://minnit.chat/c/nostal?embed${isDjBgActive ? '&transparent=1' : ''}&nickname=`}
              style={{
                border: 'none',
                width: '100%',
                height: '100%',
                minHeight: '100%',
                flex: 1,
                display: 'block',
                backgroundColor: isDjBgActive ? 'transparent' : 'rgba(0,0,0,0.85)',
              }}
              data-minnitchatembed="1"
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default LiveStageSection;
